import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { SkeletonLine } from 'components/theme/Skeleton/SkeletonLine';
import { PrimaryButton } from 'components/theme/Button';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { formatLink } from 'utils';
import {
  EXTERNAL_CTA_LINK,
  EXTERNAL_CTA_EMAIL,
  EXTERNAL_CTA_PHONE,
} from 'types/experience';
import { IFeature } from 'types/brandSettings.types';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import useCustomHistory from 'hooks/use-custom-history';
import { analyticsManager } from 'features/analytics/services/AnalyticsManager';
import {
  ExperiencesBodySidebar,
  ExternalLinkContainer,
  ExternalLink,
} from './styles';
import SessionPrice from './sub-components/SessionPrice';
import DateView from './sub-components/DateView';
import AdditionalReminderSection from './AdditionalReminderSection';

const RECOMMENDATIONS_PATH = '/e/recommendations';

interface OpenExperienceSidebarProps {
  availableDates: string[];
  containsRemindersText: boolean;
  experience: {
    hidePrice: boolean;
    loading: boolean;
    type: string;
    title: string;
    healthAndSafety: string;
    currency?: string;
    externalField?: {
      type: string;
      value: string;
    };
    id: string;
    customCallToAction: string | null;
  };
  isBookable: boolean;
  onNextDateRange: () => void;
  selectedEventSession: Record<string, unknown>;
  isPrivateMode: boolean;
}

const OpenExperienceView = ({
  availableDates,
  containsRemindersText,
  experience,
  isBookable,
  onNextDateRange,
  selectedEventSession,
  isPrivateMode,
}: OpenExperienceSidebarProps) => {
  const { t: translate } = useTranslation();
  const isLinkOutEnabled = useBrandToggleFeature(
    IFeature.LINK_OUT_RECOMMENDATIONS,
  );
  const { push: customPush } = useCustomHistory();

  const redirectingCtaExternal = () => {
    const externalCtaValue = experience?.externalField?.value;
    const externalCtaType = experience?.externalField?.type;

    if (isEqual(externalCtaType, EXTERNAL_CTA_EMAIL)) {
      const EMAIL_SUBJECT = `${experience?.title} Inquiry`;
      return `mailto:${externalCtaValue}?subject=${EMAIL_SUBJECT}`;
    }

    if (isEqual(externalCtaType, EXTERNAL_CTA_PHONE)) {
      return `tel:${externalCtaValue}`;
    }

    return formatLink(externalCtaValue);
  };

  const isExternalTypeLink = () =>
    isEqual(experience?.externalField?.type, EXTERNAL_CTA_LINK);

  if (isBookable) {
    return null;
  }

  const handleButtonClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    analyticsManager.sendEvent(
      ANALYTICS_EVENT.OPEN_EXPERIENCE_CLICK_BOOK_BUTTON,
      {
        [ANALYTICS_PROPERTY.CtaUrl]: experience?.externalField?.value,
      },
    );

    if (isLinkOutEnabled && experience?.externalField?.value) {
      event.preventDefault();
      window.open(redirectingCtaExternal(), '_blank', 'noreferrer');
      customPush({ pathname: RECOMMENDATIONS_PATH });
    }
  };

  return (
    <ExperiencesBodySidebar>
      {!experience.loading ? (
        <SessionPrice
          experience={experience}
          currency={experience?.currency}
          selectedSession={selectedEventSession}
          isPrivateBooking={isPrivateMode}
          hidePrice={experience?.hidePrice}
        />
      ) : (
        <SkeletonLine translucent style={{ height: '23px' }} />
      )}
      {!experience.loading ? (
        <DateView
          type={experience.type}
          experience={experience}
          availableDates={availableDates}
          onNextDateRange={onNextDateRange}
        />
      ) : (
        <SkeletonLine translucent />
      )}

      {!experience.loading ? (
        experience?.externalField?.value && (
          <ExternalLinkContainer>
            <ExternalLink
              href={redirectingCtaExternal()}
              target={isExternalTypeLink() ? '_blank' : '_self'}
              rel="noreferrer"
              onClick={handleButtonClick}
            >
              <PrimaryButton big className="experience-view__book-button">
                {isLinkOutEnabled && experience?.externalField?.value
                  ? translate('learnMore')
                  : experience.customCallToAction || translate('book')}
              </PrimaryButton>
            </ExternalLink>
          </ExternalLinkContainer>
        )
      ) : (
        <SkeletonLine translucent />
      )}

      {!experience.loading ? (
        <AdditionalReminderSection
          containsRemindersText={containsRemindersText}
          experience={experience}
        />
      ) : (
        <SkeletonLine translucent />
      )}
    </ExperiencesBodySidebar>
  );
};

export default OpenExperienceView;
